import { useWorkspaceId } from "../../hooks/useWorkspaceId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ResponseStatus } from "../use-add-custom-fulfilment/useAddCustomFulfilment";
import { useHttpTaxManagerAppHeaders } from "../utils/useHttpTaxManagerAppHeaders";
import { BASE_API_URL, HttpError } from "../utils/useTaxManagerAppQueryClient";

interface UpdateActivityRequest {
    activityId: string;
    isDone?: boolean;
    levyId: string;
    notes?: string;
    taxPayerWorkspaceId: string;
    title?: string;
    year: number;
}

export function useUpdateActivity() {
    const { t } = useTranslation();
    const workspaceId = useWorkspaceId();
    const headers = useHttpTaxManagerAppHeaders({ method: "PATCH" });
    const queryClient = useQueryClient();

    const mutation = useMutation<ResponseStatus, Error, UpdateActivityRequest>({
        mutationFn: async (request) => {
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/fulfilments/${request.levyId}/years/${request.year}/tax-payers/${request.taxPayerWorkspaceId}/activities/${request.activityId}`,
                {
                    headers,
                    method: "PATCH",
                    body: JSON.stringify({
                        isDone: request.isDone,
                        notes: request.notes,
                        title: request.title,
                    }),
                },
            );

            if (response.status === ResponseStatus.CONFLICT) {
                return ResponseStatus.CONFLICT;
            }

            if (!response.ok) {
                throw new HttpError(response);
            }

            return ResponseStatus.OK;
        },
        onSuccess: (status) => {
            if (status !== ResponseStatus.OK) return;
            queryClient.invalidateQueries({
                queryKey: ["useGetFulfilmentActivities"],
            });
            queryClient.invalidateQueries({
                queryKey: ["useGetFulfilmentDetail"],
            });
            queryClient.invalidateQueries({ queryKey: ["useGetFulfilments"] });
            queryClient.invalidateQueries({ queryKey: ["useGetKPIs"] });
        },
        meta: {
            errorMessage: t("errors.messages.updateActivity"),
        },
    });

    return mutation;
}
